import apiClient from '@/services/api'
import type {
  ApiEnvelope,
  Paginated,
  ApiCity,
  ApiState,
  ApiCategory,
  ApiContactInfo,
  ApiFilterOptions,
  ApiMenuCounts,
  ApiPostPropertyOptions,
  ApiPostPropertyCategoryField,
  GroupedSuggestions,
  SuggestionItem,
  DetectedLocation,
  ApiMapPoint,
  ApiMagazine,
  ApiMediaVideo,
  ApiMediaCategory,
  ApiMediaDetail,
} from '@/types/api'
import type { DashboardData, InquiryLead, MyProperty, SiteVisitLead } from '@/types/dashboard'
import type { Property, PropertyDetail } from '@/types/property'
import type { PostPropertyEditData } from '@/types/postProperty'
import type { BlogPost, FAQ, FreshProjectSlide } from '@/types/home'
import type { PremiumPackage, PremiumPaymentConfig, RazorpayOrderPayload } from '@/types/premium'
import type { ApiSiteSettings } from '@/types/settings'
import type { LegalBlock } from '@/types/legal'
import type { AchievementAward } from '@/types/achievement'
import type { CustomerTestimonial } from '@/types/testimonial'
import type { MaterialCostItem, RealEstateBookPayload } from '@/types/tools'
import type { CourseChapter, DigitalCourse, MyCoursesPayload } from '@/types/course'

/**
 * Typed client for the React Web API (`/api/web/v1`).
 *
 * `apiClient.baseURL` is already `/api/web/v1` (see lib/constants), so every
 * path here is relative to that. Each method unwraps the standard envelope
 * and returns just `data`.
 */
async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await apiClient.get<ApiEnvelope<T>>(url, { params })
  return res.data.data
}

async function post<T>(url: string, body?: unknown): Promise<T> {
  const res = await apiClient.post<ApiEnvelope<T>>(url, body)
  return res.data.data
}

async function put<T>(url: string, body?: unknown): Promise<T> {
  const res = await apiClient.put<ApiEnvelope<T>>(url, body)
  return res.data.data
}

async function patch<T>(url: string, body?: unknown): Promise<T> {
  const res = await apiClient.patch<ApiEnvelope<T>>(url, body)
  return res.data.data
}

export interface ApiUser {
  id: number
  name: string
  email: string
  mobile: string
  isOtpVerified: boolean
  needsProfile: boolean
  image: string | null
}

export interface AuthPayload {
  token: string
  user: ApiUser
  isNewUser: boolean
}

export interface RequestOtpPayload {
  mobile: string
}

export interface VerifyOtpPayload {
  mobile: string
  otp: string
  name?: string
  email?: string
}

export interface UpdateProfilePayload {
  name?: string
  email?: string
}

export interface HomeApiData {
  premiumProjects: Property[]
  recommendedForSale: Property[]
  recommendedForRent: Property[]
  freshProjectSlides: FreshProjectSlide[]
  popularCities: ApiCity[]
  categories: ApiCategory[]
  blogs: BlogPost[]
}

export interface ApiLocality {
  id: number
  name: string
  slug: string
  city: string
  avgPrice: string | null
  count: number
}

export interface ContactPayload {
  name: string
  mobile: string
  email?: string
  subject?: string
  message: string
}

export interface InquiryPayload {
  propertyId?: number
  name: string
  mobile: string
  email?: string
  message?: string
  city?: string
}

export interface SiteVisitPayload {
  propertyId: number
  name?: string
  mobile?: string
  message?: string
  date?: string
  time?: string
}

export const webApi = {
  home: (params?: { cityId?: number }) => get<HomeApiData>('/home', params),
  properties: (params?: Record<string, unknown>) => get<Paginated<Property>>('/properties', params),

  seoResolve: (path: string) =>
    get<{
      path: string | null
      canonical: string | null
      indexable: boolean
      reason: string | null
      resolution: {
        type: string | null
        cityId: number | null
        citySlug: string | null
        areaId: number | null
        areaSlug: string | null
        categoryId: number | null
        categorySlug: string | null
        intent: string | null
        canonicalPath: string | null
        isValid: boolean
        reason: string | null
        bhk?: number | null
      }
      qualification: Record<string, unknown>
      listingFilters: {
        city?: string
        intent?: 'buy' | 'rent' | 'commercial'
        category?: string[]
        area?: string[]
        bhk?: string[]
      } | null
      seo: {
        title: string
        description: string
        h1: string
        keywords: string
      } | null
    }>('/seo/resolve', { path }),
  propertiesMap: (params?: { city?: string; intent?: string; verified?: boolean; limit?: number }) =>
    get<ApiMapPoint[]>('/properties/map', params),
  property: (slug: string) => get<PropertyDetail>(`/properties/${slug}`),
  search: (params?: Record<string, unknown>) => get<Paginated<Property>>('/search', params),
  searchSuggestions: (params: {
    q: string
    city?: string
    intent?: string
    premium?: boolean
    category?: string
    limit?: number
  }) => get<GroupedSuggestions>('/search/suggestions', params),
  searchTrending: (params?: { city?: string; limit?: number }) =>
    get<SuggestionItem[]>('/search/trending', params),
  searchRecent: (params?: { limit?: number }) => get<SuggestionItem[]>('/search/recent', params),
  filterOptions: (params?: { city?: string }) => get<ApiFilterOptions>('/filter-options', params),
  menuCounts: (params?: { city?: string }) => get<ApiMenuCounts>('/menu-counts', params),
  postPropertyOptions: () => get<ApiPostPropertyOptions>('/post-property-options'),
  postPropertyCategoryFields: (categoryId: number) =>
    get<{ fields: ApiPostPropertyCategoryField[] }>('/post-property-category-fields', { categoryId }),
  propertyAmenities: () => get<string[]>('/property-amenities'),
  cities: (params?: { stateId?: number; withListings?: boolean }) =>
    get<ApiCity[]>('/cities', {
      ...(params?.stateId ? { stateId: params.stateId } : {}),
      ...(params?.withListings ? { withListings: 1 } : {}),
    }),
  states: () => get<ApiState[]>('/states'),
  localities: (params?: { city?: string; cityId?: number }) =>
    get<ApiLocality[]>('/localities', {
      ...(params?.city ? { city: params.city } : {}),
      ...(params?.cityId ? { cityId: params.cityId } : {}),
    }),

  nearbyProperties: (params: {
    lat: number
    lng: number
    radiusKm?: number
    perPage?: number
    excludeId?: number
    builder?: string
    category?: number
  }) => get<Paginated<Property>>('/properties/nearby', params),
  reverseGeocode: (params: { lat: number; lng: number }) =>
    get<DetectedLocation | null>('/location/reverse-geocode', params),
  currentLocation: (params?: { lat?: number; lng?: number }) =>
    get<{ detected: DetectedLocation | null }>('/location/current', params),
  locationSearch: (params: { q: string; city?: string; cityId?: number; limit?: number }) =>
    get<{ results: { placeId?: string; lat: number | null; lng: number | null; displayName: string; area: string | null }[] }>(
      '/location/search',
      params,
    ),
  locationPlace: (params: { placeId: string }) =>
    get<{ placeId: string; displayName: string; area: string | null; lat: number; lng: number }>(
      '/location/place',
      params,
    ),
  blogs: (params?: Record<string, unknown>) => get<Paginated<BlogPost>>('/blogs', params),
  blog: (slug: string) => get<BlogPost>(`/blogs/${slug}`),
  faqs: () => get<FAQ[]>('/faqs'),
  materialCosts: () => get<MaterialCostItem[]>('/material-costs'),
  realEstateBook: () => get<RealEstateBookPayload>('/realestate-book'),
  course: (id: number) => get<DigitalCourse>(`/courses/${id}`),
  myCourses: (userId: number) =>
    get<MyCoursesPayload>('/courses/my', { userId }),
  courseChapter: (chapterId: number, userId: number) =>
    get<CourseChapter>(`/courses/chapters/${chapterId}`, { userId }),
  magazines: () => get<ApiMagazine[]>('/magazines'),
  media: (params?: Record<string, unknown>) => get<Paginated<ApiMediaVideo>>('/media', params),
  mediaFeatured: (params?: { limit?: number }) => get<ApiMediaVideo[]>('/media/featured', params),
  mediaCategories: () => get<ApiMediaCategory[]>('/media/categories'),
  mediaDetail: (slug: string) => get<ApiMediaDetail>(`/media/${slug}`),
  testimonials: (params?: { type?: 'text' | 'video' }) =>
    get<CustomerTestimonial[]>('/testimonials', params),
  achievements: () => get<AchievementAward[]>('/achievements'),
  settings: (params?: { cityId?: number }) => get<ApiSiteSettings>('/settings', params),
  contactInfo: (params?: { cityId?: number }) => get<ApiContactInfo>('/contact-info', params),
  privacyPolicy: (params?: { lang?: string }) => get<LegalBlock[]>('/privacy-policy', params),
  termsAndConditions: (params?: { lang?: string }) => get<LegalBlock[]>('/terms-and-condition', params),

  premiumPackages: () =>
    get<{ marketingPackages: PremiumPackage[]; otherProducts: PremiumPackage[] }>('/premium-packages'),
  premiumPackage: (id: number) => get<PremiumPackage>(`/premium-packages/${id}`),
  premiumPaymentConfig: () => get<PremiumPaymentConfig>('/premium-packages/payment-config'),
  createPremiumOrder: (body: {
    plan_id: number
    name: string
    phone: string
    email?: string
    city: string
    property_location?: string
    property_type?: string
    expected_price?: string
  }) => post<RazorpayOrderPayload>('/premium-packages/orders', body),
  verifyPremiumPayment: (body: {
    transaction_id: number
    razorpay_order_id: string
    razorpay_payment_id: string
    razorpay_signature: string
  }) =>
    post<{
      redirectPath: string
      packageCategory?: 'marketing' | 'other' | 'course'
      packageName?: string
      txnId?: string
      message?: string
    }>('/premium-packages/verify-payment', body),
  reportPremiumPaymentFailure: (body: Record<string, unknown>) =>
    post<{ redirectPath: string }>('/premium-packages/payment-failure', body),
  submitPremiumManualProof: (formData: FormData) =>
    apiClient
      .post<
        ApiEnvelope<{
          txnId: string
          redirectPath: string
          message: string
          packageCategory?: 'marketing' | 'other' | 'course'
          packageName?: string
        }>
      >('/premium-packages/manual-submission', formData, {
          transformRequest: [(data, headers) => {
            if (data instanceof FormData) {
              delete headers['Content-Type']
            }
            return data
          }],
        },
      )
      .then((res) => res.data.data),

  contact: (data: ContactPayload) => apiClient.post('/contact', data),
  inquiry: (data: InquiryPayload) => apiClient.post('/inquiry', data),
  propertyContactClick: (propertyId: number, method: 'phone' | 'whatsapp') =>
    apiClient.post(`/properties/${propertyId}/contact-click`, { method }),
  siteVisit: (data: SiteVisitPayload) => apiClient.post('/site-visit', data),
  siteVisitAccept: (id: number) => apiClient.post(`/site-visits/${id}/accept`),
  siteVisitReject: (id: number, data?: { reason?: string }) =>
    apiClient.post(`/site-visits/${id}/reject`, data ?? {}),
  siteVisitReschedule: (id: number, data: { date: string; time: string; note?: string }) =>
    apiClient.post(`/site-visits/${id}/reschedule`, data),
  siteVisitConfirmReschedule: (id: number) =>
    apiClient.post(`/site-visits/${id}/confirm-reschedule`),
  siteVisitRejectReschedule: (id: number, data?: { note?: string }) =>
    apiClient.post(`/site-visits/${id}/reject-reschedule`, data ?? {}),
  siteVisitCancel: (id: number, data?: { note?: string }) =>
    apiClient.post(`/site-visits/${id}/cancel`, data ?? {}),

  feedbackNonce: () => get<{ nonce: string; expiresInMs: number }>('/feedback/nonce'),
  submitFeedback: (data: { name: string; rating: number; comment: string; nonce: string; company?: string }) =>
    post<null>('/feedback', data),

  homeLoanNonce: () => get<{ nonce: string; expiresInMs: number }>('/home-loan/nonce'),
  submitHomeLoanLead: (data: {
    type: 'emi' | 'eligibility'
    name: string
    mobile: string
    email?: string
    dateOfBirth?: string
    employmentType?: string
    monthlyIncome?: number
    existingEmis?: number
    loanAmount?: number
    propertyValue?: number
    downPayment?: number
    creditScoreRange?: string
    interestRate?: number
    tenureYears?: number
    coApplicantName?: string
    coApplicantIncome?: number
    eligibilityScore?: number
    inputs?: Record<string, unknown>
    result?: Record<string, unknown>
    nonce: string
    company?: string
  }) => post<{ id: number }>('/home-loan', data),

  requestOtp: (data: RequestOtpPayload) => post<{ isNewUser: boolean }>('/auth/request-otp', data),
  verifyOtp: (data: VerifyOtpPayload) => post<AuthPayload>('/auth/verify-otp', data),
  me: () => get<ApiUser>('/me'),
  updateProfile: (data: UpdateProfilePayload) => put<ApiUser>('/me', data),
  logout: () => post<null>('/auth/logout'),

  dashboard: () => get<DashboardData>('/dashboard'),
  myProperties: (params?: Record<string, unknown>) => get<Paginated<MyProperty>>('/my-properties', params),
  updateMyPropertyStatus: (
    id: number,
    action: 'activate' | 'deactivate' | 'sold' | 'available',
  ) =>
    patch<{ propertyId: number; isActive: boolean; isSold: boolean; status: string }>(
      `/my-properties/${id}/status`,
      { action },
    ),
  myInquiries: (params?: Record<string, unknown>) => get<Paginated<InquiryLead>>('/my-inquiries', params),
  mySiteVisits: (params?: Record<string, unknown>) => get<Paginated<SiteVisitLead>>('/my-site-visits', params),

  wishlistIds: () => get<{ ids: number[] }>('/wishlist/ids'),
  wishlist: (params?: Record<string, unknown>) => get<Paginated<Property>>('/wishlist', params),
  toggleWishlist: (propertyId: number) =>
    post<{ propertyId: number; liked: boolean }>('/wishlist/toggle', { propertyId }),

  createProperty: (body: Record<string, unknown>) =>
    post<{ propertyId: number }>('/properties', body),

  propertyForEdit: (id: number) => get<PostPropertyEditData>(`/properties/${id}/edit`),

  updateProperty: (id: number, body: Record<string, unknown>) =>
    put<{ propertyId: number }>(`/properties/${id}`, body),

  uploadPropertyPhotos: (propertyId: number, formData: FormData) =>
    apiClient
      .post<ApiEnvelope<{ propertyId: number }>>(`/properties/${propertyId}/photos`, formData, {
        transformRequest: [(data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type']
          }
          return data
        }],
      })
      .then((res) => res.data.data),
}
